import { useState } from 'react';
import { Link } from 'react-router-dom';
import { allProducts } from '../../../mocks/allProducts';

// 根据产品ID获取对应的详情页链接
const getProductDetailLink = (productId: string): string => {
  // A系列产品
  if (productId.startsWith('a')) {
    return '/a-range-details#models';
  }
  // M系列产品
  if (productId.startsWith('m')) {
    return '/m-range-details#models';
  }
  // X系列产品 (including Y6 and V6)
  if (productId.startsWith('x') || productId.startsWith('y') || productId.startsWith('v')) {
    return '/x-range-details#models';
  }
  // Z系列产品
  if (productId.startsWith('z')) {
    return '/z-range-details#models';
  }
  return '/contact';
};

export default function AllProductsSection() {
  const [selectedProduct, setSelectedProduct] = useState(allProducts[0]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  // Combine regular images and floor plans into one array
  const allImages = [
    ...selectedProduct.images,
    ...(selectedProduct.floorPlans || [])
  ];

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center cursor-pointer"
          onClick={() => setFullscreenImage(null)}
        >
          <img
            src={fullscreenImage}
            alt="Fullscreen view"
            className="max-w-full max-h-full object-contain p-4"
          />
          <button 
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 rounded-full text-white hover:bg-white/20 transition-all"
            onClick={() => setFullscreenImage(null)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
      )}

      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Desktop: Two Column Layout */}
        <div className="hidden lg:grid lg:grid-cols-12 gap-8">
          {/* Left Column: Product List */}
          <div className="lg:col-span-4 space-y-3">
            <div className="bg-black/40 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6 shadow-[0_0_30px_rgba(0,217,255,0.1)]">
              <h3 className="text-xl font-medium text-cyan-400 mb-4">All Products</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {allProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => {
                      setSelectedProduct(product);
                      setSelectedImageIndex(0);
                    }}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all cursor-pointer ${
                      selectedProduct.id === product.id
                        ? 'bg-cyan-500/20 border border-cyan-400/50 shadow-[0_0_20px_rgba(0,217,255,0.3)]'
                        : 'bg-black/20 border border-gray-700/30 hover:bg-cyan-500/10 hover:border-cyan-500/30'
                    }`}
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800">
                      <img
                        src={product.thumbnail}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className={`font-medium mb-1 ${
                        selectedProduct.id === product.id ? 'text-cyan-400' : 'text-white'
                      }`}>
                        {product.name}
                      </h4>
                      <p className="text-xs text-gray-500">{product.category}</p>
                    </div>
                    {selectedProduct.id === product.id && (
                      <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(0,217,255,0.8)]"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Product Details */}
          <div className="lg:col-span-8">
            <div className="bg-black/40 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 shadow-[0_0_30px_rgba(0,217,255,0.1)]">
              {/* Main Image */}
              <div 
                className="mb-6 rounded-xl overflow-hidden bg-gray-900 w-full h-96 cursor-pointer"
                onClick={() => setFullscreenImage(allImages[selectedImageIndex])}
              >
                <img
                  src={allImages[selectedImageIndex]}
                  alt={selectedProduct.name}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Image Thumbnails - Including Floor Plans */}
              <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
                {allImages.map((image, index) => {
                  const isFloorPlan = index >= selectedProduct.images.length;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer transition-all relative ${
                        selectedImageIndex === index
                          ? 'ring-2 ring-cyan-400 shadow-[0_0_15px_rgba(0,217,255,0.5)]'
                          : 'ring-1 ring-gray-700 hover:ring-cyan-500/50'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${selectedProduct.name} ${isFloorPlan ? 'floor plan' : 'view'} ${index + 1}`}
                        className={`w-full h-full ${isFloorPlan ? 'object-contain p-1 bg-white' : 'object-contain bg-gray-800'}`}
                      />
                      {isFloorPlan && (
                        <div className="absolute bottom-0 left-0 right-0 bg-cyan-500/80 text-black text-[8px] text-center py-0.5 font-medium">
                          PLAN
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Product Info */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <h3 className="text-3xl font-light text-white">{selectedProduct.name}</h3>
                    <span className="text-cyan-400 text-lg font-bold" style={{ textShadow: '0 0 20px rgba(0,217,255,0.8), 0 0 40px rgba(0,217,255,0.4)' }}>{selectedProduct.price}</span>
                  </div>
                  <p className="text-cyan-400 text-sm">{selectedProduct.category}</p>
                </div>

                <p className="text-gray-300 leading-relaxed">{selectedProduct.description}</p>

                <div>
                  <h4 className="text-lg font-medium text-white mb-3">Primary Use Case</h4>
                  <p className="text-gray-400">{selectedProduct.useCase}</p>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-white mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {selectedProduct.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3 text-gray-300">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0 shadow-[0_0_8px_rgba(0,217,255,0.8)]"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-4 pt-4">
                  <Link to={getProductDetailLink(selectedProduct.id)} className="flex-1 px-6 py-3 bg-transparent border border-cyan-500/50 text-white font-medium rounded-full hover:border-cyan-400 hover:shadow-[0_0_20px_rgba(0,217,255,0.3)] transition-all whitespace-nowrap cursor-pointer text-center">
                    More Details
                  </Link>
                  <Link to={`/contact?model=${encodeURIComponent(selectedProduct.name)}`} className="flex-1 px-6 py-3 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer text-center">
                    Request Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Stacked Layout */}
        <div className="lg:hidden space-y-6">
          {/* Product Selector - Horizontal Scroll with Arrow Indicator */}
          <div className="bg-black/40 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-4 shadow-[0_0_30px_rgba(0,217,255,0.1)]">
            <h3 className="text-lg font-medium text-cyan-400 mb-3">Select Product</h3>
            <div className="relative">
              <div className="overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                <div className="flex gap-3" style={{ width: 'max-content' }}>
                  {allProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => {
                        setSelectedProduct(product);
                        setSelectedImageIndex(0);
                      }}
                      className={`flex-shrink-0 w-20 p-2 rounded-xl transition-all cursor-pointer ${
                        selectedProduct.id === product.id
                          ? 'bg-cyan-500/20 border border-cyan-400/50 shadow-[0_0_15px_rgba(0,217,255,0.3)]'
                          : 'bg-black/20 border border-gray-700/30'
                      }`}
                    >
                      <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-800 mb-1.5">
                        <img
                          src={product.thumbnail}
                          alt={product.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <h4 className={`text-[10px] font-medium text-center leading-tight line-clamp-2 ${
                        selectedProduct.id === product.id ? 'text-cyan-400' : 'text-white'
                      }`}>
                        {product.name}
                      </h4>
                    </button>
                  ))}
                </div>
              </div>
              {/* Right Arrow Indicator */}
              <div className="absolute right-0 top-0 bottom-2 w-12 flex items-center justify-center pointer-events-none bg-gradient-to-l from-black/60 to-transparent">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-cyan-400/20 backdrop-blur-sm animate-pulse" style={{ boxShadow: '0 0 20px rgba(0,217,255,0.5)' }}>
                  <i className="ri-arrow-right-s-line text-cyan-400 text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="bg-black/40 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6 shadow-[0_0_30px_rgba(0,217,255,0.1)]">
            {/* Main Image - Click to Fullscreen */}
            <div 
              className="mb-4 rounded-xl overflow-hidden bg-gray-900 w-full h-64 cursor-pointer"
              onClick={() => setFullscreenImage(allImages[selectedImageIndex])}
            >
              <img
                src={allImages[selectedImageIndex]}
                alt={selectedProduct.name}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Image Thumbnails - Including Floor Plans - Horizontal Scroll */}
            <div className="relative mb-6">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {allImages.map((image, index) => {
                  const isFloorPlan = index >= selectedProduct.images.length;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden cursor-pointer transition-all relative ${
                        selectedImageIndex === index
                          ? 'ring-2 ring-cyan-400 shadow-[0_0_15px_rgba(0,217,255,0.5)]'
                          : 'ring-1 ring-gray-700'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${isFloorPlan ? 'Plan' : 'View'} ${index + 1}`}
                        className={`w-full h-full ${isFloorPlan ? 'object-contain p-1 bg-white' : 'object-contain bg-gray-800'}`}
                      />
                      {isFloorPlan && (
                        <div className="absolute bottom-0 left-0 right-0 bg-cyan-500/80 text-black text-[7px] text-center py-0.5 font-medium">
                          PLAN
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-4">
              <div>
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h3 className="text-2xl font-light text-white">{selectedProduct.name}</h3>
                  <span className="text-cyan-400 text-base font-bold" style={{ textShadow: '0 0 20px rgba(0,217,255,0.8), 0 0 40px rgba(0,217,255,0.4)' }}>{selectedProduct.price}</span>
                </div>
                <p className="text-cyan-400 text-sm">{selectedProduct.category}</p>
              </div>

              <p className="text-gray-300 text-sm leading-relaxed">{selectedProduct.description}</p>

              <div>
                <h4 className="text-base font-medium text-white mb-2">Primary Use Case</h4>
                <p className="text-gray-400 text-sm">{selectedProduct.useCase}</p>
              </div>

              <div>
                <h4 className="text-base font-medium text-white mb-2">Key Features</h4>
                <ul className="space-y-2">
                  {selectedProduct.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-300 text-sm">
                      <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-1.5 flex-shrink-0 shadow-[0_0_8px_rgba(0,217,255,0.8)]"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Link to={getProductDetailLink(selectedProduct.id)} className="w-full px-6 py-3 bg-transparent border border-cyan-500/50 text-white font-medium rounded-full hover:border-cyan-400 hover:shadow-[0_0_20px_rgba(0,217,255,0.3)] transition-all whitespace-nowrap cursor-pointer text-center">
                  More Details
                </Link>
                <Link to={`/contact?model=${encodeURIComponent(selectedProduct.name)}`} className="w-full px-6 py-3 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer text-center">
                  Request Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 217, 255, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 217, 255, 0.5);
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
